import type { GroupPrivate } from '@repo/types/groups';

import { useGetAssignedGroupsInfinite } from '@repo/api/group/useGetAssignedGroupsInfinite';
import { getGroupPrefetch } from '@repo/api/group/useGetGroupPrefetch';
import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { useQueryClient } from '@tanstack/react-query';
import { UsersIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import GroupCard from '@/components/features/groups/GroupCard';
import GroupCardSkeleton from '@/components/features/groups/GroupCardSkeleton';
import GroupsHeader from '@/components/features/groups/layout/GroupsHeader';
import { Button } from '@/components/ui/button';
import { EmptyState, EmptyStateCircles, EmptyStateContent, EmptyStateDescription, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

export default function GroupsPage() {
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState<string>('');
  const searchDebouncedValue = useDebounceValue(searchValue, 400);

  const { data, isLoading, isFetching, isSuccess, isError, error, fetchNextPage, hasNextPage, refetch } = useGetAssignedGroupsInfinite(searchDebouncedValue);

  const { data: groups, isEmpty: groupsIsEmpty } = useInfiniteQueryResult(data, searchDebouncedValue);

  const next = async () => {
    await fetchNextPage();
  };

  const prefetch = async (id: number, groupData: GroupPrivate) => {
    getGroupPrefetch({ id: id, initialData: groupData, queryClient });
  };

  useEffect(() => {
    if (isError) {
      toasterMessageHandler(baseErrorMessages, error, {
        action: (
          <Button
            size={'sm'}
            className={'ml-auto'}
            variant={'tertiary'}
            onClick={() => {
              void refetch();
            }}
          >
            Ponów
          </Button>
        ),
      });
    }
  }, [isError, error, refetch]);

  return (
    <div className={'base-direction-col'}>
      <GroupsHeader onSearchChange={setSearchValue} />
      {isSuccess && groupsIsEmpty && (
        <EmptyState className="w-full">
          <EmptyStateCircles />
          <FeaturedIcon size={'lg'}>
            <UsersIcon />
          </FeaturedIcon>
          <EmptyStateContent>
            <EmptyStateTitle>Brak Grup</EmptyStateTitle>
            <EmptyStateDescription>Nie jesteś przypisany do żadnej z grup</EmptyStateDescription>
          </EmptyStateContent>
        </EmptyState>
      )}
      <div className={'relative grid w-full grid-cols-[repeat(auto-fill,minmax(18rem,1fr))] grid-rows-[auto] gap-3 md:gap-4'}>
        {isLoading && (
          <>
            <GroupCardSkeleton />
            <GroupCardSkeleton />
            <GroupCardSkeleton />
            <GroupCardSkeleton />
            <GroupCardSkeleton />
          </>
        )}
        {groups.map((group) => (
          <GroupCard key={group.id} group={group} onFocus={() => void prefetch(group.id, group)} onMouseEnter={() => void prefetch(group.id, group)} />
        ))}
        <InfiniteScroll hasNextPage={hasNextPage} isLoading={isLoading} isFetching={isFetching} next={next} threshold={0.65} root={null}>
          {hasNextPage && !isError && <GroupCardSkeleton />}
        </InfiniteScroll>
      </div>
    </div>
  );
}
